<template>
  <div class="movie-player-container">
    <div class="movie-player">
      <div class="top-bar-area" v-if="movieList.isCurrentMovieListReady" ref="refTopBarArea">
        <div class="top-bar view-mode-full">
          <div
            v-if="!state.movieList.listSelection.showMovieListSelect"
            class="ts-loc-car-disp"
            @click="state.movieList.listSelection.showMovieListSelect = true"
          >
            <span class="ib ts" v-if="!movieList.isCurrentMovieListSearchTypeRoute">
              {{dtFormat(state.movieList.listSelection.currentMovieList?.startTs, 'yyyy/mm/dd HH:MM:SS')}}~
            </span>
            <span class="ib ts" v-if="movieList.isCurrentMovieListSearchTypeRoute">
              再生時間 {{dtFormat(state.movieList.listSelection.currentMovieList?.endTs, 'HH:MM:SS')}}
            </span>
            <div class="ib location ml4">
              <span class="ib road-name">
                {{state.movieList.listSelection.currentMovieList?.startKp.roadNameDispShort}}
              </span>
              <span class="ib direction">
                {{state.movieList.listSelection.currentMovieList?.startKp.direction}}
              </span>
              <span
                class="ib place-name"
                v-if="state.movieList.listSelection.currentMovieList?.startKp.placeName !== 'main_line'"
              >
                {{state.movieList.listSelection.currentMovieList?.startKp.placeName}}
              </span>
              <span class="ib kp">
                <i class="fas fa-location-arrow"></i>
                {{state.movieList.listSelection.currentMovieList?.startKp.kp.toFixed(1)}}
              </span>
            </div>
            <div class="ib car-name ml4" v-if="!movieList.isCurrentMovieListSearchTypeRoute">
              <span>
                (車番: {{state.movieList.listSelection.currentMovieList?.carName}})
              </span>
            </div>
          </div>
          <div v-if="state.movieList.listSelection.showMovieListSelect">
            <select
              v-model="state.movieList.listSelection.currentMovieListId"
              class="custom-select ml-select ib"
              @change="onCurrentMovieListIdChangeBySelect"
            >
              <template v-if="!movieList.isCurrentMovieListSearchTypeRoute">
                <option v-for="e in state.movieList.listSelection.currentMovieLists" :key="e.id" :value="e.id">
                  {{e.dateLocationCarDisp}}
                </option>
              </template>
              <template v-if="movieList.isCurrentMovieListSearchTypeRoute">
                <option v-for="e in state.movieList.listSelection.currentMovieLists" :key="e.id" :value="e.id">
                  {{e.dateLocationDisp}}
                </option>
              </template>
            </select>
            <button
              class="btn btn-sm btn-secondary ml4"
              @click="state.movieList.listSelection.showMovieListSelect = false"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="toggle-button mr16">
          <div class="btn-group btn-group-toggle">
            <label class="btn btn-info btn-sm active">
              <input type="radio" name="options" checked="" autocomplete="off">三面
            </label>
            <label
              class="btn btn-info btn-sm"
              :class="{disabled: movieList.isCurrentMovieListSearchTypeRoute}"
              v-tooltip.bottom="compareModeTooltip"
            >
              <input
                type="radio"
                name="options"
                :disabled="movieList.isCurrentMovieListSearchTypeRoute"
                autocomplete="off"
                @click="switchToCompareMode"
              >比較
            </label>
          </div>
        </div>
        <div class="close-button" @click="closeMoviePlayer">
          <i class="fas fa-times fa-2x"> </i>
        </div>
      </div>
      <div class="video-area" ref="refVideoArea">
        <div class="videos" :style="{ height: state.ui.styles.videoHeight }">
          <MoviePlayerVideo
            v-for="vid in [playback.vid1, playback.vid2, playback.vid3]" :key="vid"
            ref="refMoviePlayerVideos"
            :vid="vid"
            :player-type="FULL_PLAYER_TYPE"
            :current-movie="state.movieList.current.currentMovie"
            :movie-file-url="playback.selectUrlForVid(vid)"
            :is-current-movie-ready="state.movieList.current.isCurrentMovieReady"
            :is-downloading-movie-file="state.ui.isDownloadingMovieFile"
            :is-downloading-screenshot="state.ui.isDownloadingScreenshot"
            :video-control-properties="state.playback.videoControlProperties"
            :disable-volume="vid !== playback.vid2"
            :is-front="vid === playback.vid2"
            :sync-current-time="state.playback.currentTime"
            :title-bar-texts="state.playback.titleBarTexts"
            :has-continued-movies="movieList.hasContinuedMovies"
            :is-movie-ended="state.movieList.current.isMovieEnded"
            @start-download-movie-file="download.onStartDownloadMovieFile"
            @finish-download-movie-file="download.onFinishDownloadMovieFile"
            @start-download-screenshot="download.onStartDownloadScreenshot"
            @finish-download-screenshot="download.onFinishDownloadScreenshot"
            @vjs-time-update="playback.onVjsTimeUpdate"
            @loaded-data="playback.onVjsLoadedData"
            @vjs-error="playback.onVjsError"
            @play="playback.playUI"
            @pause="playback.pauseUI"
            @play-previous-movie="playback.playPreviousMovie"
            @play-next-movie="playback.playNextMovie"
            @tick-forward="playback.tickMovieForward"
            @tick-backward="playback.tickMovieBackward"
            @seek-change="playback.emitSeekChange"
            @prepared="playback.onVjsPrepared"
            @play-continued-movies="movieList.onPlayContinuedMovies"
            @playmode-change="playback.playModeChange"
          />
        </div>
      </div>
      <div class="info-area mt16" :style="{ height: state.ui.styles.infoHeight }">
        <div class="row1">
          <div class="map-area">
            <ExtremeMap
              ref="refExtremeMap"
              :map-essentials="extremeMapEssentials"
              :disable-movie-layer-interaction="true"
              @click-map="map.seekByMapClick"
              @click-item="map.onMapItemClicked"
              @all-deselected="map.onMapAllDeselected"
            />
          </div>
          <div class="control-area ml8">
            <div>
              <MoviePlayerControlArea
                ref="refControlArea"
                control-area-id="basic"
                view-mode="full"
                :play-speed="state.playback.videoControlProperties.playSpeed"
                :is-play-backward="state.playback.videoControlProperties.isPlayBackward"
                :is-playing="state.playback.isPlaying"
                :current-time-disp="movieList.playerControlCurrentTimeDisp"
                :start-time-disp="state.movieList.timeDisp.startTimeDisp"
                :end-time-disp="state.movieList.timeDisp.endTimeDisp"
                :seek-min="state.playback.seek.min"
                :seek-max="state.playback.seek.max"
                :seek-current="state.playback.seek.current"
                :seek-step="state.playback.seek.step"
                @play="playback.playMovie"
                @pause="playback.pauseMovie"
                @seek-change="playback.seekBySeekBar"
                @tick-backward="playback.tickMovieBackward"
                @tick-forward="playback.tickMovieForward"
                @play-previous-movie="playback.playPreviousMovie"
                @play-next-movie="playback.playNextMovie"
                @set-play-speed="playback.setPlaySpeed"
                @set-volume="playback.setVolume"
                @playmode-change="playback.playModeChange"
              />
            </div>
            <div class="mt8">
              <MovieMetaInfoArea
                ref="refMetaInfoArea"
                v-if="movieList.isCurrentMovieListReady"
                view-mode="full"
                :movie="state.movieList.current.currentMovie"
                :movie-geo-index="state.geo.currentMgi"
                :movie-current-dt="state.geo.currentMovieCurrentDt"
                :enable-sensor-chart="false"
                :enable-time-seek="!movieList.isCurrentMovieListSearchTypeRoute"
                :location-disp="state.geo.locationDisp"
                @start-time-edit="playback.pauseUI"
                @move-to-time="playback.seekByTimeText"
                @edit-movie-tags="tagModal.startEditMovieTags"
              />
            </div>
            <div class="map-elem-info-area full-mode mt8">
              <div class="confirm-create" v-if="!state.geo.mapElemInfo">
                <img class="map-elem-icon" :src="map.mapElemIconPath">
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="map.startCreateMapElem"
                  :disabled="!state.geo.currentMgi"
                >
                  現在位置に付箋を追加
                </button>
              </div>
              <MapElemInfoNew
                v-else
                :elem="state.geo.mapElemInfo"
                :visible-layers="map.visibleGeoItemLayers"
                :force-show-cancel-button="true"
                :create-failed="state.geo.createMapElemFailed"
                @create="map.createMapElem"
                @cancel-edit="state.geo.mapElemInfo = null"
              />
            </div>
            <div class="button-row mt8">
              <div class="sensor-chart-btn-area">
                <template v-if="!movieList.isCurrentMovieListSearchTypeRoute">
                  <button
                    class="btn btn-sm btn-secondary"
                    v-if="!state.ui.showSensorChart"
                    @click="sensor.openSensorChart"
                  >
                    三軸センサー表示
                  </button>
                  <button
                    class="btn btn-sm btn-secondary"
                    v-if="state.ui.showSensorChart"
                    @click="sensor.closeSensorChart"
                  >
                    三軸センサー非表示
                  </button>
                </template>
              </div>
              <div class="ifd-link-area full-mode" v-if="!!infraDoctor.ifdLink">
                <button class="btn btn-sm btn-info" @click="infraDoctor.openInfraDoctor">
                  インフラドクターを開く
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="row2 mt8">
          <div class="sensor-chart-area" v-show="state.ui.showSensorChart">
            <ThreeAxisSensorChart :sensor-data="state.ui.sensorData" @click="sensor.onSensorChartClick" />
          </div>
        </div>
      </div>
    </div>
    <div class="movie-player-backdrop"></div>
    <TagModal
      v-if="state.ui.movieTagModal.show"
      class="z-index-3000"
      title="動画タグの編集"
      v-model="state.ui.movieTagModal.tags"
      @close="tagModal.updateMovieTags"
      @dismiss="state.ui.movieTagModal.show = false"
    />
  </div>
</template>

<script setup lang="ts">
import MoviePlayerControlArea from 'src/components/lib/MoviePlayerControlArea/index.vue';
import MovieMetaInfoArea from 'src/components/lib/MovieMetaInfoArea/index.vue';
import MapElemInfoNew from 'src/components/Top/mapElemInfoComponents/MapElemInfoNew.vue';
import MoviePlayerVideo from 'src/components/lib/MoviePlayer/MoviePlayerVideo.vue';
import ThreeAxisSensorChart from 'src/components/lib/ThreeAxisSensorChart/index.vue';
import ExtremeMap from 'src/components/lib/ExtremeMap/index.vue';
import type { BasicPlayerProps, BasicPlayerEmit } from 'src/models/moviePlayer';
import { dtFormat } from 'src/lib/dateHelper';
import { FULL_PLAYER_TYPE } from 'src/consts/movie_player';
import { useBasicPlayerState } from 'src/components/lib/MoviePlayer/BasicMoviePlayer/composables/useBasicPlayerState';
import { useBasicPlayer } from 'src/components/lib/MoviePlayer/BasicMoviePlayer/composables/useBasicPlayer';
import { useMoviePlayerDownloadFlags } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerDownloadFlags';
import {
  useBasicPlayerPlayback,
} from 'src/components/lib/MoviePlayer/BasicMoviePlayer/composables/useBasicPlayerPlayback';
import { useMoviePlayerSensorChart } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerSensorChart';
import { useMoviePlayerTagModal } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerTagModal';
import { useMoviePlayerMapLinkage } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMapLinkage';
import { useMoviePlayerMovieList } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerMovieList';
import { useMoviePlayerInfraDoctor } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerInfraDoctor';

const props = defineProps<BasicPlayerProps>();
const emit = defineEmits<BasicPlayerEmit>();
const playerState = useBasicPlayerState();
const {
  state,
  refExtremeMap,
  refTopBarArea,
  refVideoArea,
  refControlArea,
  refMoviePlayerVideos,
  refMetaInfoArea,
  masters,
} = playerState;

// 分割composableの構築順序: 後段は前段の関数をDIで受け取るため、この順序を変更すると未定義参照になる。
const download = useMoviePlayerDownloadFlags(state);
const playback = useBasicPlayerPlayback(state, { refMoviePlayerVideos, refControlArea });
const sensor = useMoviePlayerSensorChart(state, { seekByTimeText: playback.seekByTimeText });
const tagModal = useMoviePlayerTagModal(state, { pauseUI: playback.pauseUI });
const map = useMoviePlayerMapLinkage(state, {
  refExtremeMap, refMoviePlayerVideos, parentGeoItemsMeta: props.parentGeoItemsMeta,
  setCurrentMovie: playback.setCurrentMovie, pauseUI: playback.pauseUI,
});
const movieList = useMoviePlayerMovieList(state, {
  masters, refMetaInfoArea, setCurrentMovie: playback.setCurrentMovie, updateMap: map.updateMap,
  resetSensorChart: sensor.setSensorChartUIStateToDefault, playUI: playback.playUI,
});
const infraDoctor = useMoviePlayerInfraDoctor(state);

const sub = { download, playback, sensor, tagModal, map, movieList, infraDoctor };
const {
  compareModeTooltip,
  switchToCompareMode,
  closeMoviePlayer,
  shortcutKeyAction,
  onCurrentMovieListIdChangeBySelect,
} = useBasicPlayer(props, emit, playerState, sub);

defineExpose({
  shortcutKeyAction,
});
</script>

<style lang="scss"  scoped>
.movie-player-container {
  position: absolute;
  top: 0;
  left: 0;
}
.movie-player-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2040;
  width: 100vw;
  height: 100vh;
  background-color: #000000;
  opacity: 0.5;
}
.movie-player {
  position: fixed;
  top: 6px;
  left: 8px;
  bottom: 6px;
  right: 8px;
  z-index: 2050;
  background-color: #ffffff;
  padding: 8px;

  .top-bar-area {
    display: flex;
    .top-bar {
      flex-grow: 1;
      &.view-mode-full {
        font-size: 1.1rem;
        .ts-loc-car-disp {
          display: inline-block;
          border-radius: 4px;
          padding: 0 4px;
          cursor: pointer;
          &:hover { background-color: #dfdfdf; }
          .kp .fa-location-arrow {
            font-size: 1.0rem;
          }
        }
        .ml-select {
          width: 600px;
          max-width: 65%;
          padding: 2px 6px;
          height: 32px;
          margin-top: -2px;
        }
      }
    }
    .close-button {
      width: 30px;
      text-align: center;
      cursor: pointer;
    }
  }
  .videos {
    display: flex;
    margin: 4px 0 4px 0;
  }
  .info-area {
    overflow: scroll;
    .row1 {
      display: flex;
    }
    .row2 {
      position: relative;
    }
    .map-area {
      border: 1px solid #dddddd;
      flex-grow: 1;
    }
    .control-area {
      .map-elem-info-area {
        border: 1px dashed #7ec3d9;
        .confirm-create {
          padding: 6px 8px;
        }
        .map-elem-icon {
          width: 42px;
        }
      }
      .button-row {
        display: flex;
        flex-wrap: nowrap;
        justify-content: space-between;
      }
    }
    .sensor-chart-area {
      border: 1px solid #dddddd;
    }
  }

  .movie-player-control-area.full-mode {
    @media only screen and (min-width: 1200px) {
      min-width: 600px;
      max-width: 700px;
    }
  }
  .movie-meta-info-area.full-mode {
    @media only screen and (min-width: 1200px) {
      min-width: 600px;
      max-width: 700px;
    }
  }
}
</style>
